import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import SafarGlimpseLogo from './SafarGlimpseLogo';
import DesignTokens from '../../styles/DesignTokens';

const FooterContainer = styled.footer`
  background: rgba(5, 12, 28, 0.96);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${DesignTokens.spacing['3xl']} 0 ${DesignTokens.spacing.lg};
  margin-top: 0;
  position: relative;

  @media (max-width: 768px) {
    padding-bottom: 80px;
  }

  @supports not (backdrop-filter: blur(1px)) {
    background: #050c1c;
  }
`;

const FooterContent = styled.div`
  max-width: ${props => props.theme.container?.xl || '1200px'};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing?.md || '1rem'};
  width: 100%;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${DesignTokens.spacing.xl};
  margin-bottom: ${DesignTokens.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${DesignTokens.spacing.lg};
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h4`
  font-family: ${props => props.theme.fonts.heading};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  font-size: ${DesignTokens.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: ${DesignTokens.spacing.md};
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  margin-bottom: ${DesignTokens.spacing.sm};
  font-size: ${DesignTokens.typography.fontSize.sm};
  transition: color ${DesignTokens.transition.normal} ease;
  line-height: ${DesignTokens.typography.lineHeight.relaxed};

  &:hover {
    color: #4285F4;
  }
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin-bottom: ${DesignTokens.spacing.sm};
  font-size: ${DesignTokens.typography.fontSize.sm};
`;

const Logo = styled.div`
  display: inline-block;
  margin-bottom: ${DesignTokens.spacing.md || '1rem'};
  line-height: 0;
  overflow: visible;
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: ${DesignTokens.spacing.lg || '1.5rem'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.md || '1rem'};
  margin-top: ${DesignTokens.spacing.xl || '2rem'};

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: ${DesignTokens.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
`;

const FooterLinks = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.lg};
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const FooterBottomLink = styled(Link)`
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: ${DesignTokens.typography.fontSize.sm};
  transition: color ${DesignTokens.transition.normal} ease;

  &:hover {
    color: #4285F4;
  }
`;

function Footer() {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // Hide on full-screen layout pages (explorer home, itinerary details)
  if (location.pathname === '/' || location.pathname === '/explore') return null;
  if (/^\/itineraries\/.+/.test(location.pathname)) return null;

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          {/* About Section */}
          <FooterSection>
            <Logo>
              <SafarGlimpseLogo variant="dark" height={48} />
            </Logo>
            <FooterText>
              Discover the world through immersive travel experiences.
              Learn about famous destinations and plan your next adventure!
            </FooterText>
            <FooterText>
              Bringing travel discovery and education to everyone.
            </FooterText>
          </FooterSection>

          {/* Quick Links */}
          <FooterSection>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/heritage">Heritage Destinations</FooterLink>
            <FooterLink to="/places">All Destinations</FooterLink>
            <FooterLink to="/itineraries">Recommendations</FooterLink>
            <FooterLink to="/guides">Travel Guides</FooterLink>
            <FooterLink to="/about">About Us</FooterLink>
          </FooterSection>

          {/* Popular Destinations */}
          <FooterSection>
            <FooterTitle>Popular Destinations</FooterTitle>
            <FooterLink to="/places?search=Eiffel Tower">Eiffel Tower, France</FooterLink>
            <FooterLink to="/places?search=Taj Mahal">Taj Mahal, India</FooterLink>
            <FooterLink to="/places?search=Great Wall">Great Wall of China</FooterLink>
            <FooterLink to="/places?search=Colosseum">Colosseum, Italy</FooterLink>
            <FooterLink to="/places?search=Machu Picchu">Machu Picchu, Peru</FooterLink>
            <FooterLink to="/places">View All Destinations</FooterLink>
          </FooterSection>

          {/* Contact & Support */}
          <FooterSection>
            <FooterTitle>Contact & Support</FooterTitle>
            <FooterText>
              <strong>Email:</strong>{' '}
              <a 
                href="mailto:contact@safarglimpse.com" 
                style={{ color: 'inherit', textDecoration: 'none' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                contact@safarglimpse.com
              </a>
            </FooterText>
            <FooterText>
              For feedback, brand collaboration, or general inquiries.
            </FooterText>
            <FooterLink to="/contact">Contact Us</FooterLink>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            © {currentYear} Safar Glimpse (SafarGlimpse.com). All Rights Reserved.
            <br />
            <span style={{ fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
              Proprietary content protected by copyright. Unauthorized copying prohibited.
            </span>
            <span style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              <strong>Data Sources & Attribution:</strong> Content from <a href="https://www.wikipedia.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Wikipedia</a> and <a href="https://www.wikidata.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Wikidata</a> under <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>CC BY-SA 3.0</a>. Images from <a href="https://unsplash.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Unsplash</a> and <a href="https://commons.wikimedia.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Wikimedia Commons</a>.
            </span>
          </Copyright>
          <FooterLinks>
            <FooterBottomLink to="/terms">Terms of Service</FooterBottomLink>
            <FooterBottomLink to="/privacy">Privacy Policy</FooterBottomLink>
            <FooterBottomLink to="/privacy/dashboard">Privacy Dashboard</FooterBottomLink>
            <FooterBottomLink to="/copyright">Copyright & Licensing</FooterBottomLink>
            <FooterBottomLink to="/contact">Contact</FooterBottomLink>
          </FooterLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
